/* global Matomo */

import { pipe, has, replace, toLower, toString } from "ramda";

import { absolutePageToRelative } from "~/utils";

// const isDefined = value => value !== undefined && value !== null;

function trimTrailingSlash(path) {
  return path.endsWith("/") ? path.slice(0, -1) : path;
}

// function appendTrailingSlash(path) {
//   return path.endsWith("/") ? path : `${path}/`;
// }

const slugify = pipe(
  toString,
  toLower,
  replace(/\s+/g, "-"), // Replace spaces with -
  replace(/[^\w-]+/g, ""), // Remove all non-word chars
  replace(/--+/g, "-"), // Replace multiple - with single -
  replace(/^-+/, ""), // Trim - from start of text
  replace(/-+$/, "") // Trim - from end of text
);

const noop = () => {};

// Group exports to prevent eslint error: import/no-mutable-exports
// https://github.com/import-js/eslint-plugin-import/blob/main/docs/rules/no-mutable-exports.md
const matomo = {
  trackHomePageVisit: noop,
  trackPageChange: noop,
  trackMaterialClick: noop
};

function getTitle() {
  return window.getClientConfiguration(document.title, ["book", "pdfinfo", "Title"]);
}

function isDrm() {
  return window.getClientConfiguration(false, ["book", "drm"]);
}

function isDls() {
  return window.getClientConfiguration(false, ["book", "settings", "features", "dls"]);
}

function getGroups() {
  return window.getClientConfiguration([], ["profile", "groups"]);
}

function isDeveloper() {
  const groups = getGroups();

  return groups.includes("buchinfinitelicense");
}

function getUserRole() {
  if (isDrm()) {
    const groups = getGroups();

    if (groups.includes("buchinfinitelicense")) {
      return "entwickler";
    }

    if (groups.includes("Schuladministrator")) {
      return "schuladministrator";
    }

    if (groups.includes("Lehrer")) {
      return "lehrer";
    }

    if (groups.includes("Schüler")) {
      return "schüler";
    }

    if (groups.includes("User")) {
      return "user";
    }

    return "keine";
  }

  return "unbekannt";
}

function getLicenseType() {
  if (isDrm()) {
    return "drm";
  }

  return "no_drm";
}

function getArticle() {
  const article = window.getClientConfiguration("0000-000", ["book", "id"]);

  if (isDls()) {
    return `ug_${article}`;
  }

  return article;
}

function buildTrackingUrl(...segments) {
  const siteUrl = "https://buch.mildenberger-digital.de/";

  const singleSegments = segments.flat().flatMap(segment => String(segment).split("/"));
  const trackingUrl = [trimTrailingSlash(siteUrl), "_details", getLicenseType(), "buecher", getArticle()]
    .concat(singleSegments)
    .filter(Boolean)
    .join("/");

  const url = trimTrailingSlash(trackingUrl);

  return url;
}

function buildHomeTrackingUrl() {
  const siteUrl = "https://buch.mildenberger-digital.de/";

  const trackingUrl = [trimTrailingSlash(siteUrl), "_oeffnungen", getLicenseType(), "buecher", getArticle()]
    .filter(Boolean)
    .join("/");

  const url = trimTrailingSlash(trackingUrl);

  return url;
}

function initializeTracking() {
  const tracker = Matomo.getAsyncTracker();

  const trackAction = action => {
    const tracking = window.getClientConfiguration({}, ["tracking"]);
    const dimensions = window.getClientConfiguration({}, ["tracking", "dimensions"]);
    const isOptedOut = tracker.isUserOptedOut();

    const skipTracking = isDeveloper() || isOptedOut;

    if (skipTracking === false) {
      tracker.setCustomDimension(dimensions.userRole, getUserRole());

      tracker.setTrackerUrl(tracking.host);
      tracker.setSiteId(tracking.siteId);

      action();

      tracker.deleteCustomDimension(dimensions.userRole);
    }
  };

  const trackPageView = (...segments) => {
    const url = buildTrackingUrl(segments);

    trackAction(() => {
      tracker.setCustomUrl(url);
      tracker.trackPageView(getTitle());
    });
  };

  const trackHomePageView = () => {
    const url = buildHomeTrackingUrl();

    trackAction(() => {
      tracker.setCustomUrl(url);
      tracker.trackPageView(getTitle());
    });
  };

  matomo.trackHomePageVisit = () => {
    trackHomePageView();
  };

  matomo.trackPageChange = page => {
    const relativePage = absolutePageToRelative(page);
    trackPageView(`seite`, relativePage);
  };

  matomo.trackMaterialClick = material => {
    let slug = slugify(material.title);

    if (material.page !== -1) {
      const relativePage = absolutePageToRelative(material.page);
      slug = `${slug}_${relativePage}`;
    }

    trackPageView(`material`, slug);
  };
}

if (has("Matomo", window)) {
  initializeTracking();
} else {
  window.initializeTracking = initializeTracking;
}

export const { trackHomePageVisit, trackPageChange, trackMaterialClick } = matomo;
